// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from './client';
import { client } from './client.gen';
import type {
  CreateChatCompletionData,
  CreateChatCompletionErrors,
  CreateChatCompletionResponses,
  CreateEmbeddingsData,
  CreateEmbeddingsErrors,
  CreateEmbeddingsResponses,
  GetHealthData,
  GetHealthErrors,
  GetHealthResponses,
  ListModelsData,
  ListModelsResponses,
} from './types.gen';

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean
> = Options2<TData, ThrowOnError> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Create chat completion
 *
 * Generate text responses using a specified language model.
 *
 * ## Advanced Modalities
 *
 * ### Ensemble Mode
 * Sends the same prompt to multiple models and combines responses intelligently:
 * - **Ensemble Cost**: Input tokens charged once; output tokens for each model
 * - **Use Cases**: High-stakes decisions requiring diverse perspectives
 * - **Response Merging**: Results are combined based on agreement and confidence
 *
 * ### Meta-Metacognition
 * Enables multi-layer reasoning where models analyze their own reasoning:
 * - **Layer 1**: Initial response generation
 * - **Layer 2**: Self-reflection on reasoning process
 * - **Layer 3**: Meta-analysis of reflection quality
 * - **Cost Impact**: 3x token usage (one per layer)
 * - **Benefit**: More nuanced, self-aware responses
 *
 * ### Monte Carlo Analysis
 * Runs multiple probabilistic iterations to handle uncertainty:
 * - **Iterations**: Configurable (typically 5-20 runs)
 * - **Token Cost**: `num_iterations × base_tokens`
 * - **Use Cases**: Probability estimation, scenario analysis, risk assessment
 * - **Convergence**: Results stabilize with more iterations
 * - **Standard Deviation**: Measure of response variability
 *
 */
export const createChatCompletion = <ThrowOnError extends boolean = false>(
  options: Options<CreateChatCompletionData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateChatCompletionResponses,
    CreateChatCompletionErrors,
    ThrowOnError
  >({
    security: [
      { scheme: 'bearer', type: 'http' },
      { name: 'cf-aig-authorization', type: 'apiKey' },
    ],
    url: '/chat/completions',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Create embeddings
 *
 * Generate vector embeddings for text, enabling semantic search and similarity analysis.
 *
 * ## Use Cases
 * - Semantic search across emotions and reflections
 * - Clustering similar emotional states
 * - Finding related tools or coping strategies
 * - Recommendation systems
 *
 * ## Token Counting
 * Embeddings count tokens the same as chat completions.
 * All models are self-hosted with no per-token costs.
 *
 */
export const createEmbeddings = <ThrowOnError extends boolean = false>(
  options: Options<CreateEmbeddingsData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateEmbeddingsResponses,
    CreateEmbeddingsErrors,
    ThrowOnError
  >({
    security: [
      { scheme: 'bearer', type: 'http' },
      { name: 'cf-aig-authorization', type: 'apiKey' },
    ],
    url: '/embeddings',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * List available models
 *
 * Retrieve information about all available models and their capabilities.
 *
 * Returns model details including:
 * - Model ID and name
 * - Provider (Cloudflare Workers or Cloud Run)
 * - Capabilities (chat, embeddings, vision, audio, translation)
 * - Token limits (input/output context windows)
 * - Rate limits
 * - Latest update
 *
 * ## Text Models
 * - mistral-7b, llama-70b, llama-13b, glm-4.7, qwen-edit
 * - tinyllama-1.1b, deepseek-1.5b, deepseek-3b
 *
 * ## Vision/Audio/Translation Models
 * - flux-4b (image generation)
 * - vibevoice-9b (TTS)
 * - translategemma-4b (translation)
 *
 */
export const listModels = <ThrowOnError extends boolean = false>(
  options?: Options<ListModelsData, ThrowOnError>
) =>
  (options?.client ?? client).get<ListModelsResponses, unknown, ThrowOnError>({
    security: [
      { scheme: 'bearer', type: 'http' },
      { name: 'cf-aig-authorization', type: 'apiKey' },
    ],
    url: '/models',
    ...options,
  });

/**
 * Health check
 *
 * Check the health status of the AI Gateway and all connected providers.
 *
 * Returns status information including:
 * - Gateway status (operational/degraded/offline)
 * - Provider status (each provider's current status)
 * - Rate limit status (current usage vs limits)
 * - Token counter status
 * - Cache status
 *
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>
) =>
  (options?.client ?? client).get<
    GetHealthResponses,
    GetHealthErrors,
    ThrowOnError
  >({
    security: [
      { scheme: 'bearer', type: 'http' },
      { name: 'cf-aig-authorization', type: 'apiKey' },
    ],
    url: '/health',
    ...options,
  });
